import { FormRule, Skeleton, Alert, Switch, Typography } from "antd";
import { ComponentType, ReactNode } from "react";
import dayjs, { Dayjs } from "dayjs";
import { AirportType, FlightType, SegmentType } from ".";
import { COUNTRIES_CODES, NATIONALITIES } from "src/Seed/Countries/Countries";
import { BackpackIcon, BaggageIcon, ClassIcon, VoucherIcon, XIcon, MealIcon, SideSeatIcon, PassengerIcon, ChildrenIcon, BabyIcon } from "src/assets/Icons";
import { Details, Passengers, Payment, SSR } from "../Pages/JourneyDetails/Steps";
import NoSSR from "src/Modules/Flights/Pages/JourneyDetails/components/NoSSR/NoSSR";
import i18n from "src/i18n";
import { formatDate } from "src/utility";
import { PASSENGER_CLASSES } from "./SearchPage";
import BaggageGuidelines from "../Pages/JourneyDetails/Steps/SSR/components/BaggageGuidelines/BaggageGuidelines";
import SeatUseIcon from "../Pages/JourneyDetails/Steps/SSR/components/PlaneSeating/components/SeatIcon/SeatUseIcon";
import {
  SEAT_FILL_CLASSNAME,
} from "../Pages/JourneyDetails/Steps/SSR/components/PlaneSeating/components/RowItem/seatVisualState";
import { track } from "@amplitude/analytics-browser";
import PassengerDateField from "../Pages/JourneyDetails/Steps/Passengers/components/PassengerFields/components/PassengerDateField/PassengerDateField";
import PassengerTextField from "../Pages/JourneyDetails/Steps/Passengers/components/PassengerFields/components/PassengerTextField/PassengerTextField";
import PassengerSelectField from "../Pages/JourneyDetails/Steps/Passengers/components/PassengerFields/components/PassengerSelectField/PassengerSelectField";
import PassengerGenderField from "../Pages/JourneyDetails/Steps/Passengers/components/PassengerFields/components/PassengerGenderField/PassengerGenderField";

type JourneyDetailsResponse = {
  isPriceChanged: boolean;
  isRefundable: boolean;
  isLCC: boolean;
  isPassportRequiredAtBook: boolean;
  isPassportRequiredAtTicket: boolean;
  isPanRequiredAtBook: boolean;
  isPanRequiredAtTicket: boolean;
  fare: Fare;
  originalFare: Fare;
  fareBreakdown: FareBreakdown[];
  fareRules: FareRule[];
  miniRules: MiniRule[][];
  ssr: SSR;
};

type Fare = {
  currency: string;
  baseFare: number;
  tax: number;
  otherCharges: number;
  serviceFee: number;
  additionalTxnFeePub: number;
  airlineTransFee: number;
  publishedFare: number;
  priceInPoints: number;
};

type FareBreakdown = {
  passengerType: string;
  passengerCount: number;
  baseFare: number;
  tax: number;
  yqTax: number;
  baseFarePerPax: number;
  taxPerPax: number;
};

type FareRule = {
  origin: string;
  destination: string;
  airline: string;
  fareBasisCode: string;
  fareRestriction: string | null;
  ruleDetail: string;
  inclusions: string | null;
};

type MiniRule = {
  journeyPoints: string;
  type: string;
  from: string;
  to: string;
  unit: string;
  details: string;
};

interface MealOption {
  airlineCode: string;
  flightNumber: string;
  wayType: string;
  code: string;
  description: string;
  airlineDescription: string | null;
  quantity: number;
  currency: string;
  price: number;
  origin: string;
  destination: string;
}

type MealSegment = MealOption[];

type MealDynamic = MealSegment[] | null;
interface BaggageOption {
  airlineCode: string;
  flightNumber: string;
  wayType: "FullJourney" | string;
  code: string;
  description: string;
  weight: number;
  currency: string;
  price: number;
  origin: string;
  destination: string;
  text: string | null;
}

type BaggageDynmaic = BaggageOption[][];

type SSR = {
  baggage: BaggageDynmaic;
  mealDynamic: MealDynamic;
  seatDynamic: SeatDynamic;
};

type SeatSegment = {
  segmentSeat: SegmentSeat[];
};
type SeatDynamic = SeatSegment[] | null;

type SegmentSeat = {
  rowSeats: RowSeat[];
};

type RowSeat = {
  seats: SeatType[];
};

type SeatType = {
  airlineCode: string;
  flightNumber: string;
  craftType: string;
  origin: string;
  destination: string;
  availabilityType: SeatStatusType;
  description: SeatDescriptionType;
  code: string;
  rowNo: string;
  seatNo: string | null;
  seatType: "Window" | "Aisle" | "Middle" | "NoSeat";
  seatWayType: SeatWayType;
  compartment: number;
  deck: number;
  currency: string;
  price: number;
  seatSubType: string;
};

const FARE_RULES_SKELETON = (
  <div className="flex flex-col gap-4">
    <Skeleton active title paragraph={{ rows: 6 }}></Skeleton>
    <Skeleton active title paragraph={{ rows: 3 }}></Skeleton>
    <Skeleton active title paragraph={{ rows: 7 }}></Skeleton>
  </div>
);

const FARE_BREAKDOWN_SKELETONS = (
  <div>
    <Skeleton paragraph={{ rows: 4, className: "w-full" }} active></Skeleton>
  </div>
);

type StepProps = Record<string, never>;

type JourneyStepType = {
  value: JourneyStepValue;
  label: ReactNode;
  inPageTitle?: ReactNode;
  nextButtonTitle?: ReactNode;
  prevButtonTitle?: ReactNode;
  canEnter?: (state: ValidityState) => boolean;
  canExit?: (state: ValidityState) => boolean;
  inPageSubtitle?: ReactNode;
  buttonAction?: (helpers: { onClick: () => void }) => void;
  Component?: ComponentType<StepProps>;
};
const JOURNEY_DETAILS_STEPS: JourneyStepType[] = [
  {
    value: "journeyDetails",
    get label() {
      return i18n.t("Flights.details.steps.details");
    },
    get inPageTitle() {
      return i18n.t("Flights.details.steps.reviewFlightDetails");
    },
    get inPageSubtitle() {
      return i18n.t("Flights.details.steps.reviewSubtitle");
    },
    get nextButtonTitle() {
      return i18n.t("Flights.details.steps.proceedToPassengers");
    },
    canEnter: (state) => true,
    canExit: (state) => true,
    buttonAction: ({ onClick }) => {onClick?.(); track("Proceeded To Passengers")},
    Component: Details,
  },
  {
    value: "passengers",
    get label() {
      return i18n.t("Flights.details.steps.passengers");
    },
    get inPageTitle() {
      return i18n.t("Flights.details.steps.whosTraveling");
    },
    get inPageSubtitle() {
      return (
        <Alert
          title={i18n.t("Flights.details.steps.passportWarning")}
          banner
          className="rounded-md! font-inter! text-sm! font-medium! px-5!"
        />
      );
    },
    get nextButtonTitle() {
      return i18n.t("Flights.details.steps.proceedToExtras");
    },
    canEnter: (state) => true,
    canExit: (state) => true,
    buttonAction: ({ onClick }) => onClick?.(),
    Component: Passengers,
  },
  {
    value: "ssrConfiguration",
    get label() {
      return i18n.t("Flights.details.steps.extras");
    },
    get nextButtonTitle() {
      return i18n.t("Flights.details.steps.proceedToPayment");
    },
    canEnter: (state) => state.passengers === true,
    buttonAction: ({ onClick }) => {onClick?.(); track("Proceeded To Pay")},
    Component: SSR,
  },
  {
    value: "payment",
    get label() {
      return i18n.t("Flights.details.steps.payment");
    },
    inPageTitle: null,
    inPageSubtitle: null,
    canEnter: (state) => state.passengers === true,
    nextButtonTitle: null,
    Component: Payment,
  },
];

type JourneyStepValue = "journeyDetails" | "passengers" | "ssrConfiguration" | "payment";

type ValidityState = Record<JourneyStepValue, boolean>;

const INITIAL_STEPS_VALIDATION: ValidityState = {
  passengers: false,
  journeyDetails: false,
  ssrConfiguration: false,
  payment: false,
};

const JOUNREY_DETAILS_STEPS_COUNT = JOURNEY_DETAILS_STEPS.length;

type FlightInclusionType = {
  path: string[];
  key: string;
  title?: string;
  icon: ReactNode;
  transform?: (value: any) => ReactNode;
};

const FLIGHT_INCLUSIONS: FlightInclusionType[] = [
  {
    path: ["carryOn"],
    key: "carryOn",
    transform:(value?:string)=>{
      if (!value) return null;
      const text =
        value.toLocaleLowerCase().includes("cabin") || value.toLocaleLowerCase().includes("carry")
          ? value
          : `${value} ${i18n.t("Flights.details.carryOn")}`;
      return (
        <Typography.Text ellipsis={{ tooltip: text }} className="max-w-25! text-sm! text-inherit!">
          {text}
        </Typography.Text>
      );
    },
    icon: <BackpackIcon className="h-5 w-5" />,
  },
  {
    path: ["cabin"],
    key: "cabin",
    icon: <ClassIcon className="h-5 w-5" />,
    transform: (value) => PASSENGER_CLASSES?.find((item) => item.key === value)?.label,
  },
  {
    path: ["supplierFareClass"],
    key: "supplierFareClass",
    icon: <VoucherIcon className="h-5 w-5" />,
  },
];

type FlightAddition = {
  meals?: Record<string, MealOption>;
  seats?: Record<string, SeatType>;
  baggages?: Record<string, BaggageOption>;
};

type SegmentAddition = {
  flights: {
    ssr: FlightAddition;
    flight: FlightType;
  }[];
  ssr: FlightAddition;
  segment: SegmentType;
};
type PassengerBasicData = {
  dob: string;
  email: string;
  expiryDate: Dayjs;
  firstName: string;
  lastName: string;
  gender: string;
  issueCountry: string;
  issueDate: Dayjs;
  nationality: string;
  mobileCountryCode: string;
  passportNumber: string;
  phoneNumber: string;
  title?: string;
  birthCertificate?: string;
  addressLine1?: string;
  isLeadPax?: boolean;
};

type PassengerClassificationType = "Adult" | "Child" | "Infant";

type PassengerType = {
  basicData: PassengerBasicData;
  type: PassengerClassificationType;
  segments: SegmentAddition[];
};

type PassengerInputType = {
  passengers: PassengerType[];
};

const PASSENGER_KEYS_MAP = {
  infantCount: { value: "Infant", order: 3 },
  childCount: { value: "Child", order: 2 },
  adultCount: { value: "Adult", order: 1 },
};

type FieldContext = {
  passengerType: PassengerClassificationType;
  index: number;
  form: any;
  conditionFields: {
    passportExpiry: Dayjs;
    departureDate: Dayjs;
    isLeadPax: boolean;
    isMainPassenger: boolean;
    validatingAirline: string;
    currentPassengerFormData: any;
    isLargeScreen: boolean;
  };
};

type DataOptionType = {
  name: string;
  order?:number;
  label?: string | null;
  initialValue?: any;
  component: ReactNode | ((value: FieldContext) => ReactNode);
  rules?: FormRule[] | ((value: FieldContext) => FormRule[]);
  dependencies?: string[][];
  valuePropName?: string;
  layout?: "horizontal" | "vertical";
  placement?: "header" | "body";
};

const INFANT_DATA: DataOptionType[] = [
  {
    name: "birthCertificate",
    get label() {
      return i18n.t("Flights.details.form.birthCertificateNumber");
    },
    get component() {
      return <PassengerTextField placeholder={i18n.t("Flights.details.form.enterBirthCertificateNumber")} />;
    },
    get rules() {
      return [{ required: true, message: i18n.t("Flights.details.form.birthCertificateRequired") }];
    },
  },
];

// Keeps a horizontal Form.Item's label and switch on one line (antd otherwise stacks them on narrow widths).
const INLINE_SWITCH_CLASSNAME =
  "flex! items-center! [&_.ant-form-item-row]:flex-nowrap! [&_.ant-form-item-row]:items-center! [&_.ant-form-item-label]:flex-none! [&_.ant-form-item-label]:py-0! [&_.ant-form-item-label]:ps-0! [&_.ant-form-item-label]:pe-1.5! [&_.ant-form-item-label>label]:after:hidden! [&_.ant-form-item-control]:flex-none!";

const MAIN_CONTACT_DATA: DataOptionType[] = [
  {
    name: "email",
    get label() {
      return i18n.t("Flights.details.form.email");
    },
    get component() {
      return <PassengerTextField placeholder={i18n.t("Flights.details.form.enterEmail")} />;
    },
    dependencies: [["passengers"]],
    rules: ({ conditionFields }: FieldContext) => [
      {
        required: !!(conditionFields?.isMainPassenger || conditionFields?.isLeadPax),
        message: i18n.t("Flights.details.form.mainContactEmailRequired"),
      },
      { type: "email", message: i18n.t("Flights.details.form.validEmail") },
    ],
  },
  {
    name: "phoneNumber",
    get label() {
      return i18n.t("Flights.details.form.phoneNumber");
    },
    get component() {
      return <PassengerTextField placeholder={i18n.t("Flights.details.form.enterPhoneNumber")} />;
    },
    dependencies: [["passengers"]],
    rules: ({ conditionFields }: FieldContext) => [
      {
        required: !!(conditionFields?.isMainPassenger || conditionFields?.isLeadPax),
        message: i18n.t("Flights.details.form.mainContactPhoneRequired"),
      },
      { pattern: /^\d{7,17}$/, message: i18n.t("Flights.details.form.validPhoneNumber") },
    ],
  },
];

const ADULT_DATA: DataOptionType[] = [
  {
    name: "passportNumber",
    get label() {
      return i18n.t("Flights.details.form.passportNumber");
    },
    order:1,
    get component() {
      return <PassengerTextField placeholder={i18n.t("Flights.details.form.enterPassportNumber")} />;
    },
    dependencies: [["passengers"]],
    rules: ({ form, index }: FieldContext) => [
      { required: true, message: i18n.t("Flights.details.form.passportNumberRequired") },
      {
        pattern: /^[a-zA-Z0-9]{6,9}$/,
        message: i18n.t("Flights.details.form.validPassportNumber"),
      },
      {
        validator: async (_, value: string) => {
          if (!value) return Promise.resolve();
          const passengers = form.getFieldValue(["passengers"]) || [];
          const isDuplicate = passengers.some(
            (passenger: PassengerType, passengerIndex: number) =>
              passengerIndex !== index &&
              passenger?.basicData?.passportNumber &&
              passenger.basicData.passportNumber.trim().toLowerCase() === value.trim().toLowerCase(),
          );
          if (isDuplicate) {
            return Promise.reject(i18n.t("Flights.details.form.duplicatePassportNumber"));
          }
          return Promise.resolve();
        },
      },
    ],
  },
  {
    name: "expiryDate",
    get label() {
      return i18n.t("Flights.details.form.expiryDate");
    },
    order:4,
    component: () => <PassengerDateField label={i18n.t("Flights.details.form.expiryDate")} />,
    rules: ({ conditionFields }: FieldContext) => [
      { required: true, message: i18n.t("Flights.details.form.passportExpiryRequired") },
      {
        validator: async (_, value: Dayjs) => {
          const diff = conditionFields.passportExpiry?.add(6, "M").diff(value);
          if (diff < 0) return Promise.resolve();
          return Promise.reject(
            i18n.t("Flights.details.form.expiryDateMustBeAfter", { date: formatDate(conditionFields.passportExpiry) }),
          );
        },
      },
    ],
  },
  {
    name: "issueCountry",
    order:2,
    get label() {
      return i18n.t("Flights.details.form.countryOfIssue");
    },
    get component() {
      return (
        <PassengerSelectField
          placeholder={i18n.t("Flights.details.form.enterCountryOfIssue")}
          options={COUNTRIES_CODES}
          showSearch
        />
      );
    },
    get rules() {
      return [{ required: true, message: i18n.t("Flights.details.form.countryOfIssuanceRequired") }];
    },
  },
  {
    name: "issueDate",
    get label() {
      return i18n.t("Flights.details.form.issueDate");
    },
    order:3,
    component: () => <PassengerDateField label={i18n.t("Flights.details.form.issueDate")} />,
    rules: ({ conditionFields }: FieldContext) => [
      { required: true, message: i18n.t("Flights.details.form.issueDateRequired") },
      {
        validator: async (_, value: Dayjs) => {
          const diff = value?.diff(conditionFields.departureDate);
          if (diff < 0) return Promise.resolve();
          return Promise.reject(
            i18n.t("Flights.details.form.issueDateMustBeBefore", { date: formatDate(conditionFields.departureDate) }),
          );
        },
      },
    ],
  },
];

const DEFAULT_NAME_PATTERN = /^[A-Za-z][A-Za-z .]{0,31}$/;
const DEFAULT_ALLOWED_TITLE_OPTIONS = {
  Adult: [
    { value: "Mr", get label() { return i18n.t("Flights.details.titles.mr"); } },
    { value: "Mstr", get label() { return i18n.t("Flights.details.titles.mstr"); } },
    { value: "Mrs", get label() { return i18n.t("Flights.details.titles.mrs"); } },
    { value: "Ms", get label() { return i18n.t("Flights.details.titles.ms"); } },
    { value: "Miss", get label() { return i18n.t("Flights.details.titles.miss"); } },
    { value: "DR", get label() { return i18n.t("Flights.details.titles.dr"); } },
    { value: "PROF", get label() { return i18n.t("Flights.details.titles.prof"); } },
    { value: "MST", get label() { return i18n.t("Flights.details.titles.mst"); } },
    { value: "Master", get label() { return i18n.t("Flights.details.titles.master"); } },
  ],
  Child: [{ value: "CHD", get label() { return i18n.t("Flights.details.titles.child"); } }],
  Infant: [{ value: "Inf", get label() { return i18n.t("Flights.details.titles.infant"); } }],
};

type ValidationTypes = {
  displayName: string;
  firstName?: RegExp;
  lastName?: RegExp;
  titleOptions?: {
    Adult?: { label: string; value: string }[];
    Child?: { label: string; value: string }[];
    Infant?: { label: string; value: string }[];
  };
};

const AIRLINES_VALIDATIONS: Record<string, ValidationTypes> = {
  NDC: {
    displayName: "NDC",
    firstName: /^[A-Za-z]{1,32}$/,
    lastName: /\b(?!(?:Mr|Mstr|Mrs|Ms|Miss|Master|DR|CHD|MST|Prof|Inf)\.)([A-Z][a-zA-Z]{0,31})/,
  },
  "2T": {
    displayName: "TruJet",
    lastName: /\b(?!(?:Mr|Mstr|Mrs|Ms|Miss|Master|DR|CHD|MST|Prof|Inf)\.)([A-Z][a-zA-Z]{0,31})/,
    titleOptions: {
      Adult: [
        { value: "MR", get label() { return i18n.t("Flights.details.titles.mr"); } },
        { value: "MRS", get label() { return i18n.t("Flights.details.titles.mrs"); } },
        { value: "MS", get label() { return i18n.t("Flights.details.titles.ms"); } },
      ],
      Child: [
        { value: "MISS", get label() { return i18n.t("Flights.details.titles.miss"); } },
        { value: "MSTR", get label() { return i18n.t("Flights.details.titles.mister"); } },
      ],
    },
  },
  B3: {
    displayName: "buhtan",
    lastName: /\b(?!(?:Mr|Mstr|Mrs|Ms|Miss|Master|DR|CHD|MST|Prof|Inf)\.)([A-Z][a-zA-Z]{0,31})/,
  },
  Z0: {
    displayName: "ZoomAir",
    lastName: /\b(?!(?:Mr|Mstr|Mrs|Ms|Miss|Master|DR|CHD|MST|Prof|Inf)\.)([A-Z][a-zA-Z]{0,31})/,
  },
  LB: {
    displayName: "AirCosta",
    lastName: /\b(?!(?:Mr|Mstr|Mrs|Ms|Miss|Master|DR|CHD|MST|Prof|Inf)\.)([A-Z][a-zA-Z]{0,31})/,
  },

  J9: {
    displayName: "Jazeera",
    lastName: /^[A-Za-z][A-Za-z .]{0,31}$/,
  },
  G9: {
    displayName: "AirArabia",
    lastName: /^[A-Za-z][A-Za-z .]{0,31}$/,
  },

  SG: {
    displayName: "SpiceJet",
    lastName: /^[A-Za-z][A-Za-z .]{0,31}$/,
  },
};

const SHARED_DATA: DataOptionType[] = [
  {
    name: "title",
    get label() {
      return i18n.t("Flights.details.form.title");
    },
    order:0,
    component: ({ passengerType, conditionFields }: FieldContext) => {
      const airlineKey = conditionFields.validatingAirline;
      const options = AIRLINES_VALIDATIONS[airlineKey]?.titleOptions?.[passengerType] ?? DEFAULT_ALLOWED_TITLE_OPTIONS?.[passengerType];
      const freshOptions = options?.map((option) => ({ value: option.value, label: option.label }));
      return <PassengerSelectField placeholder={i18n.t("Flights.details.form.enterTitle")} options={freshOptions} />;
    },
    rules: () => {
      return [{ required: true, message: i18n.t("Flights.details.form.titleRequired") }];
    },
  },
  {
    name: "firstName",
    get label() {
      return i18n.t("Flights.details.form.firstName");
    },
    order:1,
    get component() {
      return <PassengerTextField placeholder={i18n.t("Flights.details.form.enterFirstName")} />;
    },
    rules: ({ conditionFields }: FieldContext) => {
      let pattern;
      const airlineKey = conditionFields?.validatingAirline as keyof typeof AIRLINES_VALIDATIONS;
      pattern = AIRLINES_VALIDATIONS[airlineKey]?.firstName ?? DEFAULT_NAME_PATTERN;
      return [
        { required: true, message: i18n.t("Flights.details.form.firstNameRequired") },
        { pattern: pattern, message: i18n.t("Flights.details.form.validName") },
      ];
    },
  },
  {
    name: "lastName",
    get label() {
      return i18n.t("Flights.details.form.lastName");
    },
    get component() {
      return <PassengerTextField placeholder={i18n.t("Flights.details.form.enterLastName")} />;
    },
    order:2,
    rules: ({ conditionFields }: FieldContext) => {
      let pattern;
      const airlineKey = conditionFields.validatingAirline as keyof typeof AIRLINES_VALIDATIONS;
      pattern = AIRLINES_VALIDATIONS[airlineKey]?.lastName ?? DEFAULT_NAME_PATTERN;
      return [
        { required: true, message: i18n.t("Flights.details.form.lastNameRequired") },
        { pattern: pattern, message: i18n.t("Flights.details.form.validName") },
      ];
    },
  },
  {
    name: "nationality",
    get label() {
      return i18n.t("Flights.details.form.nationality");
    },
    order:0,
    get component() {
      return (
        <PassengerSelectField
          placeholder={i18n.t("Flights.details.form.enterNationality")}
          options={NATIONALITIES}
          showSearch
        />
      );
    },
    get rules() {
      return [{ required: true, message: i18n.t("Flights.details.form.nationalityRequired") }];
    },
  },
  {
    name: "gender",
    get label() {
      return i18n.t("Flights.details.form.gender");
    },
    order:5,
    get component() {
      return (
        <PassengerGenderField
          options={[
            { value: "male", label: i18n.t("Flights.details.form.male") },
            { value: "female", label: i18n.t("Flights.details.form.female") },
          ]}
        />
      );
    },
    get rules() {
      return [{ required: true, message: i18n.t("Flights.details.form.genderRequired") }];
    },
  },
  {
    name: "dob",
    get label() {
      return i18n.t("Flights.details.form.birthday");
    },
    order:4,
    component: () => <PassengerDateField label={i18n.t("Flights.details.form.birthday")} />,
    rules: ({ conditionFields, passengerType }) => [
      {
        required:true, message: i18n.t("Flights.details.form.dobRequired"),
      },
      {
        validator: async (_, value: Dayjs) => {
          const departure = conditionFields.departureDate;
          if (!value || !value.isValid()) return;
          if (value.isAfter(dayjs(), "day")) return Promise.reject(i18n.t("Flights.details.form.dobNotFuture"));
          const ageInMonths = departure.diff(value, "month");
          switch (passengerType) {
            case "Adult":
              return ageInMonths >= 12 * 12 ? Promise.resolve() : Promise.reject(i18n.t("Flights.details.form.adultAgeRequirement"));
            case "Child":
              return ageInMonths >= 2 * 12 && ageInMonths < 12 * 12
                ? Promise.resolve()
                : Promise.reject(i18n.t("Flights.details.form.childAgeRequirement"));
            case "Infant":
              return ageInMonths >= 0 && ageInMonths < 2 * 12
                ? Promise.resolve()
                : Promise.reject(i18n.t("Flights.details.form.infantAgeRequirement"));
            default:
              return Promise.resolve();
          }
        },
      },
    ],
  },
  {
    name: "addressLine1",
    get label() {
      return i18n.t("Flights.details.form.address");
    },
    order:3,
    get component() {
      return <PassengerTextField placeholder={i18n.t("Flights.details.form.enterAddress")} />;
    },
    rules: () => {
      return [{ required: true, message: i18n.t("Flights.details.form.addressRequired") }];
    },
  },
];

const PASSENGER_DATA_MAP = {
  Adult: [
    ...SHARED_DATA,
    ...ADULT_DATA,
    ...MAIN_CONTACT_DATA,
    {
      name: "isLeadPax",
      get label() {
        return i18n.t("Flights.details.form.setAsLead");
      },
      layout: "horizontal",
      placement: "header",
      dependencies: [["passengers"]],
      valuePropName: "checked",
      className: INLINE_SWITCH_CLASSNAME,
      component: (context: FieldContext) => {
        const passengers = context.form.getFieldValue(["passengers"]) || [];
        const adultCount = passengers.filter((p: any) => p.type === "Adult").length;
        if (adultCount <= 1) return null;
        return (
          <Switch
            onChange={(checked) => {
              if (!checked) {
                context.form.setFieldValue(["passengers", context.index, "basicData", "isLeadPax"], true);
                return;
              }
              const passengers = context.form.getFieldValue(["passengers"]) || [];
              passengers.forEach((_: any, i: number) => {
                context.form.setFieldValue(["passengers", i, "basicData", "isLeadPax"], i === context.index);
              });
            }}
          />
        );
      },
      rules: [],
    } as DataOptionType,
  ],
  Child: [...SHARED_DATA, ...ADULT_DATA],
  Infant: [...SHARED_DATA, ...INFANT_DATA],
};


type PassengerSectionConfig = {
  title: (currentPassengerFormData?: any) => ReactNode;
  subtitle: (currentPassengerFormData?: any) => ReactNode;
  inputTypes: Partial<Record<PassengerClassificationType, string[]>>;
  className?:string;
};

export const PASSENGER_SECTIONS: Record<string, PassengerSectionConfig> = {
  mainSection:{
    title:() => i18n.t("Flights.details.form.personalInformation"),
    subtitle:() => null,
    inputTypes: {
      "Adult":["title","firstName","lastName","dob","addressLine1","gender"],
      "Child":["title","firstName","lastName","dob","addressLine1","gender"],
      "Infant":["title","firstName","lastName","dob","addressLine1","gender", "birthCertificate"],
    },
  },
  secondarySection:{
    title:() => i18n.t("Flights.details.form.passportAndTravelDocuments"),
    subtitle:() => null,
    inputTypes:{
      Adult:["nationality","passportNumber","expiryDate","issueCountry","issueDate"],
      Child:["nationality","passportNumber","expiryDate","issueCountry","issueDate"],
      Infant:[]
    }
  },
  contactSection:{
    title:() => i18n.t("Flights.details.form.contactDetails"),
    subtitle:(conditionFields: any) => conditionFields?.isMainPassenger || conditionFields?.isLeadPax ? <div>{i18n.t("Flights.details.form.bookingInfoEmailNotice")}</div> : null,
    inputTypes:{
      Adult:["email","phoneNumber"],
      Child:[],
      Infant:[]
    },
    className:"bg-background-minimal!",
  }
}

type SSROptionsKeys = "seats" | "baggages" | "meals";

type SSROptionsType =
  | {
      value: SSROptionsKeys;
      label: ReactNode;
      className: string;
      backendKey: string;
      icon: ReactNode;
      ssrSelectionType: "flight" | "segment";
      isMulti: boolean;
      labelTitle?:ReactNode;
    }
  | undefined;

type SSRFlightOptionType =
  | {
      segmentIndex: number;
      flightIndex: number;
      value: string;
      origin: AirportType;
      destination: AirportType;
      duration: number;
    }
  | undefined;

const SSR_OPTIONS = [
  {
    value: "seats",
    get label() {
      return i18n.t("Flights.details.ssr.seats");
    },
    className: "me-2",
    backendKey: "seatDynamic",
    icon: <SideSeatIcon className="text-icon-active h-full w-auto" />,
    ssrSelectionType: "flight",
    isMulti: false,
  },
  {
    value: "baggages",
    get label() {
      return i18n.t("Flights.details.ssr.baggage");
    },
    get labelTitle(){
      return <BaggageGuidelines />;
    },
    className: "ms-2",
    backendKey: "baggage",
    icon: <BaggageIcon className="text-icon-active aspect-square w-full min-w-5 " />,
    ssrSelectionType: "segment",
    isMulti: false,
  },
  {
    value: "meals",
    get label() {
      return i18n.t("Flights.details.ssr.meals");
    },
    className: "ms-2",
    backendKey: "mealDynamic",
    icon: <MealIcon className="text-icon-active aspect-square w-full min-w-5"  stroke="currentcolor" strokeWidth={20} />,
    ssrSelectionType: "flight",
    isMulti: false,
  },
] as const satisfies SSROptionsType[];



const SSR_OPTION_SKELETONS = (
  <Skeleton.Input
    active
    classNames={{
      content: "rounded-full!",
    }}
  />
);

const SSR_NO_DATA = () => (
  <div className="flex h-full w-full flex-col items-center justify-center bg-white p-5 rounded-md">
    <NoSSR className="w-full! h-full!" />
    <div className="text-2xl">{i18n.t("Flights.details.ssr.extrasUnavailable")}</div>
  </div>
);

const SEAT_TYPE = {
  NotSet: 0,
  Window: 1,
  Aisle: 2,
  Middle: 3,
  WindowRecline: 4,
  WindowWing: 5,
  WindowExitRow: 6,
  WindowReclineWing: 7,
  WindowReclineExitRow: 8,
  WindowWingExitRow: 9,
  AisleRecline: 10,
  AisleWing: 11,
  AisleExitRow: 12,
  AisleReclineWing: 13,
  AisleReclineExitRow: 14,
  AisleWingExitRow: 15,
  MiddleRecline: 16,
  MiddleWing: 17,
  MiddleExitRow: 18,
  MiddleReclineWing: 19,
  MiddleReclineExitRow: 20,
  MiddleWingExitRow: 21,
  WindowReclineWingExitRow: 22,
  AisleReclineWingExitRow: 23,
  MiddleReclineWingExitRow: 24,
  WindowBulkhead: 25,
  WindowQuiet: 26,
  WindowBulkheadQuiet: 27,
  MiddleBulkhead: 28,
  MiddleQuiet: 29,
  MiddleBulkheadQuiet: 30,
  AisleBulkhead: 31,
  AisleQuiet: 32,
  AisleBulkheadQuiet: 33,
  CentreAisle: 34,
  CentreMiddle: 35,
  CentreAisleBulkHead: 36,
  CentreAisleQuiet: 37,
  CentreAisleBulkHeadQuiet: 38,
  CentreMiddleBulkHead: 39,
  CentreMiddleQuiet: 40,
  CentreMiddleBulkHeadQuiet: 41,
  WindowBulkHeadWing: 42,
  WindowBulkHeadExitRow: 43,
  MiddleBulkHeadWing: 44,
  MiddleBulkHeadExitRow: 45,
  AisleBulkHeadWing: 46,
  AisleBulkHeadExitRow: 47,
  NoSeatAtThisLocation: 48,
  WindowAisle: 49,
  NoSeatRow: 50,
  NoSeatRowExit: 51,
  NoSeatRowWing: 52,
  NoSeatRowWingExit: 53,
  WindowAisleRecline: 54,
  WindowAisleWing: 55,
  WindowAisleExitRow: 56,
  WindowAisleReclineWing: 57,
  WindowAisleReclineExitRow: 58,
  WindowAisleWingExitRow: 59,
  WindowAisleBulkhead: 60,
  WindowAisleBulkheadWing: 61,
};
type SeatKeyType = keyof typeof SEAT_TYPE;

const PLANE_SEATING_LEGEND = [
  {
    icon: <SeatUseIcon className={`h-full w-full -scale-y-100 ${SEAT_FILL_CLASSNAME.available}`} />,
    get label() {
      return <div>{i18n.t("Flights.details.ssr.seatAvailable")}</div>;
    },
  },
  {
    icon: <SeatUseIcon className={`h-full w-full -scale-y-100 ${SEAT_FILL_CLASSNAME.selected}`} />,
    get label() {
      return <div>{i18n.t("Flights.details.ssr.seatSelected")}</div>;
    },
  },
  {
    icon: (
      <span className="relative flex h-full w-full items-center justify-center">
        <SeatUseIcon className={`absolute inset-0 h-full w-full -scale-y-100 ${SEAT_FILL_CLASSNAME.unavailable}`} />
        <XIcon className="relative h-1.5 w-1.5 text-icon-disabled" />
      </span>
    ),
    get label() {
      return <span>{i18n.t("Flights.details.ssr.seatOccupied")}</span>;
    },
  },
  {
    icon: (
      <SeatUseIcon className={`h-full w-full -scale-y-100 ${SEAT_FILL_CLASSNAME.ExitRow}`} />
    ),
    get label() {
      return <div>{i18n.t("Flights.details.ssr.exitRow")}</div>;
    },
  },
];

const SEAT_STATUS = {
  NotSet: 0,
  Open: 1,
  Reserved: 3,
  Blocked: 4,
  NoSeatAtThisLocation: 5,
};

const SEAT_DESCRIPTION = {
  Included: 1,
  Purchase: 2,
};
const SEAT_WAY_TYPE = {
  Segment: 1,
  FullJourney: 2,
};
export const formatPassengers = (count: number) => {
  return [
    {
      key: "Infant",
      label: count === 1 ? i18n.t("Flights.details.passengerType.infant") : i18n.t("Flights.details.passengerType.infants"),
      icon: <BabyIcon />,
    },
    {
      key: "Child",
      label: count === 1 ? i18n.t("Flights.details.passengerType.child") : i18n.t("Flights.details.passengerType.children"),
      icon: <ChildrenIcon />,
    },
    {
      key: "Adult",
      label: count === 1 ? i18n.t("Flights.details.passengerType.adult") : i18n.t("Flights.details.passengerType.adults"),
      icon: <PassengerIcon />,
    },
  ];
};

type SeatDescriptionType = keyof typeof SEAT_DESCRIPTION;
type SeatStatusType = keyof typeof SEAT_STATUS;
type SeatWayType = 1 | 2; // 1 = Segment, 2 = FullJourney

type PaymentState = {
  pointsToPay: number;
  cashToPay: number;
};

type PaymentSelection = "PayAll" | "PayCustom";

export { JOURNEY_DETAILS_STEPS, JOUNREY_DETAILS_STEPS_COUNT, INITIAL_STEPS_VALIDATION };
export type {
  StepProps,
  JourneyDetailsResponse,
  FareBreakdown,
  FareRule,
  SeatStatusType,
  SeatDescriptionType,
  JourneyStepValue,
  ValidityState,
  SeatType,
  BaggageOption,
  SSR,
  MealDynamic,
  RowSeat,
  MealOption,
  BaggageDynmaic,
  FieldContext,
  SeatKeyType,
  PassengerClassificationType,
  PassengerInputType,
  PassengerType,
  SSROptionsKeys,
  SSRFlightOptionType,
  SSROptionsType,
  SegmentAddition,
  PassengerBasicData,
  MiniRule,
  PaymentState,
  PaymentSelection,
};
export { PLANE_SEATING_LEGEND, SEAT_DESCRIPTION, SEAT_WAY_TYPE, SEAT_STATUS, SEAT_TYPE };
export { FARE_RULES_SKELETON, FARE_BREAKDOWN_SKELETONS, FLIGHT_INCLUSIONS };
export { SSR_OPTIONS, SSR_OPTION_SKELETONS, SSR_NO_DATA };
export { PASSENGER_DATA_MAP, PASSENGER_KEYS_MAP };

export interface ApiResponse {
  data: BookResponseData;
  message: string;
  errorCode: string;
  referenceId: string;
}

export interface BookResponseData {
  isSuccess: boolean;
  pnr: string | null;
  status: string;
  ssrDenied: boolean;
  ssrMessage: string | null;
  orderId?: string;

  changeInItinerary: ChangeInItinerary;

  itinerary: Itinerary;

  errors: ApiError[];
  alerts: unknown[];

  bookingId: number;

  newFare: Fare;
}

export interface ChangeInItinerary {
  isArrivalTimeChanged: boolean;
  isDepartureTimeChanged: boolean;
  isPriceChanged: boolean;
}

export interface Itinerary {
  pnr: string;
  origin: string;
  destination: string;

  lastTicketDate: string; // ISO date
  lastVoidDate: string;

  validatingAirlineCode: string;
  ticketed: boolean;
  airlineCode: string;

  travelDate: string;
  nonRefundable: boolean;

  bookingId: number;
  isLcc: boolean;
  createdOn: string;

  isVATApplicable: boolean;
  isNewBooking: boolean;

  segments: Segment[];
  passengers: Passenger[];

  fareRules: FareRule[];
  miniFareRules: MiniFareRule[][];
}

export interface Segment {
  segmentId: number;
  noOfSeatAvailable: number;

  airline: string;
  flightNumber: string;

  origin: Airport;
  destination: Airport;

  departureTime: string;
  arrivalTime: string;

  bookingClass: string;
  arrTerminal: string;

  [key: string]: unknown;
}

export interface Airport {
  airportCode: string;
  airportName: string | null;

  cityCode: string;
  cityName: string;

  countryCode: string;
  countryName: string;

  terminal: string | null;
}

export interface Passenger {
  paxId: number;

  title: string;
  firstName: string;
  lastName: string;

  mobile1: string | null;
  mobile1CountryCode: string | null;

  mobile2: string | null;
  mobile2CountryCode: string | null;

  isLeadPax: boolean;

  fare: Fare;

  [key: string]: unknown;
}

export interface MiniFareRule {
  journeyPoints: string | null;
  type: string | null;
  from: string | null;
  to: string | null;
  unit: string | null;
  details: string | null;
}

export interface ApiError {
  userMessage: string;
  code: number;
  timeStamp: string;
  resolution: string;
}
